import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { findAnchor } from "./anchors";

/** Matches the spotlight's padding, so the ring sits on the cutout's edge. */
const PADDING_PX = 8;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

function sameBox(a: Box | null, b: Box | null): boolean {
  return (
    a === b ||
    (!!a &&
      !!b &&
      a.top === b.top &&
      a.left === b.left &&
      a.width === b.width &&
      a.height === b.height)
  );
}

/**
 * A ring on the edge of the spotlight. The cutout alone only dims what surrounds the anchor, which
 * says nothing on the dark sidebar: dimmed slate on slate is still slate.
 */
export default function TourHalo({ anchors }: { anchors: string[] }) {
  const [box, setBox] = useState<Box | null>(null);

  // Followed frame by frame: the anchor moves with scrolling, resizing and late layout alike.
  useEffect(() => {
    let frame = 0;
    const follow = () => {
      const rect = findAnchor(anchors)?.getBoundingClientRect();
      // Kept inside the viewport: an anchor flush with the edge (the sidebar) would lose a side.
      const left = rect ? Math.max(2, rect.left - PADDING_PX) : 0;
      const top = rect ? Math.max(2, rect.top - PADDING_PX) : 0;
      const next = rect
        ? {
            top: Math.round(top),
            left: Math.round(left),
            width: Math.round(
              Math.min(window.innerWidth - 2, rect.right + PADDING_PX) - left,
            ),
            height: Math.round(rect.bottom + PADDING_PX - top),
          }
        : null;
      setBox((previous) => (sameBox(previous, next) ? previous : next));
      frame = window.requestAnimationFrame(follow);
    };
    follow();
    return () => window.cancelAnimationFrame(frame);
  }, [anchors]);

  if (!box) return null;
  return createPortal(
    <div
      aria-hidden
      className="pointer-events-none fixed rounded-xl ring-2 ring-white/90 shadow-[0_0_0_6px_rgba(23,82,183,0.35)] motion-safe:animate-pulse"
      // Between Joyride's overlay (100) and its card (200).
      style={{ ...box, zIndex: 150 }}
    />,
    document.body,
  );
}
